/**
 * Per-field inline validators keyed by field ID.
 * Each function receives the current value and returns an error string
 * if invalid, or undefined if valid.
 */

// Only alphanumeric, excluding ambiguous chars I, O, Q (standard VIN spec)
const VIN_RE = /^[A-HJ-NPR-Z0-9]{17}$/i

// Must be exactly 5 digits
const ZIP_RE = /^\d{5}$/

// Single digit 0–9
const SINGLE_DIGIT_RE = /^\d$/

export const FIELD_VALIDATORS: Record<string, (value: string, context?: Record<string, string>) => string | undefined> = {
  vin(value) {
    if (!value) return undefined
    if (!/^[A-Za-z0-9]+$/.test(value)) return 'VIN must contain only letters and numbers.'
    if (value.length !== 17) return `VIN must be exactly 17 characters (${value.length}/17).`
    if (!VIN_RE.test(value)) return 'VIN contains invalid characters (I, O, Q not allowed).'
    return undefined
  },

  valuationZipCode(value) {
    if (!value) return undefined
    if (!ZIP_RE.test(value)) return 'Zip Code must be exactly 5 digits.'
    return undefined
  },

  noOfAxles(value) {
    if (!value) return undefined
    if (!SINGLE_DIGIT_RE.test(value)) return '# of Axles must be a single digit (0–9).'
    return undefined
  },

  noOfDriveAxles(value) {
    if (!value) return undefined
    if (!SINGLE_DIGIT_RE.test(value)) return '# of Drive Axles must be a single digit (0–9).'
    return undefined
  },

  noOfTagAxles(value) {
    if (!value) return undefined
    if (!SINGLE_DIGIT_RE.test(value)) return '# of Tag Axles must be a single digit (0–9).'
    return undefined
  },

  tiresFWearRmg(value) {
    if (!value) return undefined
    const n = Number(value)
    if (!Number.isInteger(n) || n < 0 || n > 100) return 'F Wear Rmg % must be a whole number between 0 and 100.'
    return undefined
  },

  tiresRWearRmg(value) {
    if (!value) return undefined
    const n = Number(value)
    if (!Number.isInteger(n) || n < 0 || n > 100) return 'R Wear Rmg % must be a whole number between 0 and 100.'
    return undefined
  },

  odometer(value) {
    if (!value) return undefined
    if (!/^\d+$/.test(value)) return 'Odometer must contain only numeric digits.'
    return undefined
  },

  totalAxleCapacity(value) {
    if (!value) return undefined
    if (!/^\d+$/.test(value)) return 'Total Axle Capacity must contain only numeric digits.'
    return undefined
  },

  dateOfLoss(value) {
    if (!value) return undefined
    const today = new Date().toISOString().split('T')[0]
    if (value > today) return 'Date of Loss cannot be a future date.'
    return undefined
  },

  dateClaimReceived(value, context) {
    if (!value) return undefined
    const today = new Date().toISOString().split('T')[0]
    if (value > today) return 'Date Claim Received cannot be a future date.'
    const dateOfLoss = context?.['dateOfLoss']
    if (dateOfLoss && value < dateOfLoss) return 'Date Claim Received must be on or after Date of Loss.'
    return undefined
  },
}

/**
 * Input restrictions applied at the keyboard/paste level to prevent
 * invalid characters from ever being entered (Zip Code and Axle fields).
 * VIN is intentionally omitted — users type freely and see inline errors.
 */
export interface InputRestriction {
  /** Block any keystroke that is not a digit 0–9 */
  digitsOnly?: boolean
  /** Native maxLength attribute value */
  maxLength?: number
}

export const INPUT_RESTRICTIONS: Record<string, InputRestriction> = {
  vin:              { maxLength: 17 },
  valuationZipCode: { digitsOnly: true, maxLength: 5 },
  noOfAxles:        { digitsOnly: true, maxLength: 1 },
  noOfDriveAxles:   { digitsOnly: true, maxLength: 1 },
  noOfTagAxles:     { digitsOnly: true, maxLength: 1 },
  tiresFWearRmg:    { digitsOnly: true, maxLength: 3 },
  tiresRWearRmg:    { digitsOnly: true, maxLength: 3 },
  odometer:         { digitsOnly: true },
  totalAxleCapacity: { digitsOnly: true },
}
